import { FC } from "react";
import { useTranslation } from "react-i18next";
import { FiCheckCircle, FiDownload, FiExternalLink } from "react-icons/fi";

import { AVAILIO_LOGO, FAIRGATE_LOGO } from "@constants/assets";
import { usePWAInstall } from "@hooks/usePWAInstall";

const KioskInstall: FC = () => {
  const { t } = useTranslation();
  const { installPWA, openPWA, pwaStatus } = usePWAInstall();

  const renderContent = () => {
    switch (pwaStatus) {
      case "running-as-pwa":
        return (
          <>
            <div className="text-center mb-4">
              <FiCheckCircle size={64} className="text-success mb-3" />
              <h1 className="kiosk-install-title fw-bold text-dark text-center mb-3">
                {t("kiosk.install.runningAsPwa.title")}
              </h1>
            </div>

            <p className="kiosk-install-description text-muted text-center mb-4 mb-md-5">
              {t("kiosk.install.runningAsPwa.description")}
            </p>

            <div className="kiosk-install-guidance p-4 mb-4 mb-md-5 border border-success">
              <h2 className="kiosk-install-guidance-title fw-bold text-success mb-3">
                {t("kiosk.install.runningAsPwa.readyTitle")}
              </h2>
              <p className="kiosk-install-guidance-text text-secondary mb-0">
                {t("kiosk.install.runningAsPwa.readyDescription")}
              </p>
            </div>
          </>
        );

      case "installed":
        return (
          <>
            <div className="text-center mb-4">
              <FiCheckCircle size={64} className="text-success mb-3" />
              <h1 className="kiosk-install-title fw-bold text-dark text-center mb-3">
                {t("kiosk.install.installed.title")}
              </h1>
            </div>

            <p className="kiosk-install-description text-muted text-center mb-4 mb-md-5">
              {t("kiosk.install.installed.description")}
            </p>

            <div className="kiosk-install-guidance p-4 mb-4 mb-md-5 border border-success">
              <h2 className="kiosk-install-guidance-title fw-bold text-success mb-3">
                {t("kiosk.install.installed.completeTitle")}
              </h2>
              <p className="kiosk-install-guidance-text text-secondary mb-0">
                {t("kiosk.install.installed.completeDescription")}
              </p>
            </div>

            <button
              onClick={openPWA}
              className="kiosk-install-button btn btn-success w-100 d-flex align-items-center justify-content-center gap-2 py-3 px-4 fw-bold border-0 rounded-3"
            >
              <FiExternalLink className="kiosk-install-button-icon" />
              {t("kiosk.install.installed.openButton")}
            </button>
          </>
        );

      case "installable":
        return (
          <>
            <h1 className="kiosk-install-title fw-bold text-dark text-center mb-3 mb-md-4">
              {t("kiosk.install.installable.title")}
            </h1>

            <p className="kiosk-install-description text-muted text-center mb-4 mb-md-5">
              {t("kiosk.install.installable.description")}
            </p>

            <div className="kiosk-install-guidance p-4 mb-4 mb-md-5">
              <h2 className="kiosk-install-guidance-title fw-bold text-dark mb-3">
                {t("kiosk.install.installable.guidanceTitle")}
              </h2>
              <p className="kiosk-install-guidance-text text-secondary mb-0">
                {t("kiosk.install.installable.guidanceDescription")}
              </p>
            </div>

            <button
              onClick={installPWA}
              className="kiosk-install-button btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2 py-3 px-4 fw-bold border-0 rounded-3"
            >
              <FiDownload className="kiosk-install-button-icon" />
              {t("kiosk.install.installable.installButton")}
            </button>
          </>
        );

      default: // not-installed
        return (
          <>
            <h1 className="kiosk-install-title fw-bold text-dark text-center mb-3 mb-md-4">
              {t("kiosk.install.notInstalled.title")}
            </h1>

            <p className="kiosk-install-description text-muted text-center mb-4 mb-md-5">
              {t("kiosk.install.notInstalled.description")}
            </p>

            <div className="kiosk-install-guidance p-4 mb-4 mb-md-5 bg-warning bg-opacity-10 border border-warning">
              <h2 className="kiosk-install-guidance-title fw-bold text-dark mb-3">
                {t("kiosk.install.notInstalled.warningTitle")}
              </h2>
              <p className="kiosk-install-guidance-text text-secondary mb-0">
                {t("kiosk.install.notInstalled.warningDescription")}
              </p>
            </div>
          </>
        );
    }
  };

  return (
    <div className="kiosk-install-container d-flex flex-column p-3 p-md-4 p-lg-5">
      {/* Header */}
      <header className="text-center mb-4 mb-md-5">
        <div className="d-flex flex-column align-items-center gap-2">
          <img
            src={AVAILIO_LOGO}
            alt={t("kiosk.install.logoAlt")}
            data-testid="fg-kiosk-install-logo"
            className="kiosk-install-logo"
          />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow-1 d-flex flex-column align-items-center justify-content-center">
        <div className="kiosk-install-content">{renderContent()}</div>
      </main>

      {/* Footer */}
      <footer className="mt-5 pt-4 text-center">
        <div className="d-flex flex-column align-items-center gap-2">
          <img
            src={FAIRGATE_LOGO}
            alt={t("kiosk.install.fairgateLogoAlt")}
            data-testid="fg-kiosk-install-footer-logo"
            className="kiosk-install-footer-logo"
          />
          <p className="kiosk-install-footer-text text-muted fw-semibold text-uppercase mb-0">
            {t("kiosk.install.brandingSubtitle")}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default KioskInstall;
